import _ from 'lodash';

import {LOAD_DATA} from '~/frontend/src/constants';
import {
    StoreState,
    AppDispatch,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src//utilities/xhr';
import setParams from '~/frontend/src/actions/panel/common/setParams';
import {
    ElusiveData,
    setElusiveData
} from '~/frontend/src/actions/elusiveData';
import {
    MergeMode,
    processData
} from '~/frontend/src/utilities/dataUtils';

export type LoadDataConfig = {
    params? : { [ key : string ] : any };
    metaDataIncludesCiData? : boolean;
    extraMetaDataKeys? : string[];
    extraDataKeys? : string[];
    extraParams? : string[];
    mergeMode? : MergeMode;
}

/**
 * Load data from backend
 *
 * @memberOf! Actions
 *
 * @param {string} command
 * @param {string} dataContext The data context
 * @param {string} componentUid The uid of the calling component
 * @param {string} [requestUid=undefined] The uid of the request
 * @param {Schema} [schema='DATA']
 * @param {LoadDataConfig} [config={}] additional configuration
 * @param {boolean} [storeLastParams=false] save stringified params within elusiveData as lastParams
 * @param {() => void} [successCallback]
 *
 * @return {Promise<boolean>}
 */
const loadData = ( command : string, dataContext : string, componentUid : string, requestUid? : string, schema : Schema = 'DATA', config : LoadDataConfig = {}, storeLastParams : boolean = false, successCallback? : ( responseData : any ) => void | Promise<void> ) : Thunk<Promise<boolean>> => async ( dispatch : AppDispatch, getState : () => StoreState ) : Promise<boolean> => {
    const totalStartTime = performance.now(),
          debug          = false,
          log : string[] = [];

    log.push( 'Action - data - loadData'.asLogLine( 100 ) );
    log.push( 'command: ' + command );
    log.push( 'componentUid: ' + componentUid );
    log.push( 'requestUid: ' + requestUid );

    dispatch( {
        type    : LOAD_DATA,
        panelKey: componentUid,
        command,
        dataContext,
        componentUid,
        requestUid,
        schema,
        config,
        storeLastParams
    } );

    if ( !requestUid ) {
        requestUid = 'loadData';
    }

    // abort running load
    if ( getState().xhr?.[ componentUid ]?.[ requestUid ] ) {
        xhr.abort( componentUid, requestUid );
    }

    // set lastParams if
    if ( storeLastParams ) {
        dispatch( setElusiveData( componentUid, {
            lastParams: 'requested'
        } ) );
    }

    let startTime = performance.now();

    const response = await xhr.post( `${BASE_URL}/api/${command}`, config.params, undefined, componentUid, requestUid, dispatch );


    log.push( `loadData: ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms` );


    let retVal = false;

    if ( response?.data.success && !response.data.errorTitle ) {

        startTime = performance.now();

        // payloadSchema from response overrides preset schema
        schema = response.data.metaData?.payloadSchema?.toUpperCase() ?? schema;

        const data                = processData( response.data.data, dataContext, schema, config.mergeMode ),
              elusiveDataContexts = [ componentUid ];

        log.push( `processData: ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms` );

        startTime = performance.now();

        // get extra params from data object
        if ( config.extraParams ) {
            dispatch( setParams( componentUid, _.pick( response.data.data, config.extraParams ) ) );
        }

        // if dataContext other than componentUid update elusive data therefore too (only data)
        if ( dataContext !== componentUid ) {
            elusiveDataContexts.push( dataContext );
        }

        for ( const elusiveDataContext of elusiveDataContexts ) {
            const elusiveData                  = getState().elusiveData?.[ elusiveDataContext ],
                  newElusiveData : ElusiveData = {
                      ...elusiveData,
                      data    : {
                          ...elusiveData?.data,
                          items     : {
                              ...elusiveData?.data?.items
                          },
                          references: {
                              ...elusiveData?.data?.references
                          }
                      },
                      metaData: {
                          ...elusiveData?.metaData,
                          items     : {
                              ...elusiveData?.metaData?.items
                          },
                          references: {
                              ...elusiveData?.metaData?.references
                          }
                      }
                  };

            for ( const itemId of data.items ) {
                newElusiveData.data!.items![ itemId ] = ( elusiveData?.data?.items?.[ itemId ] ?? 0 ) + 1;
            }
            for ( const referenceId of data.references ) {
                newElusiveData.data!.references![ referenceId ] = ( elusiveData?.data?.references?.[ referenceId ] ?? 0 ) + 1;
            }

            if ( elusiveDataContext === componentUid ) {

                // set lastParams if
                if ( storeLastParams ) {
                    newElusiveData.lastParams = config.params;
                }

                // get extra data from data object
                if ( config.extraDataKeys ) {
                    newElusiveData.data = {
                        ...newElusiveData.data,
                        ..._.pick( response.data.data, config.extraDataKeys )
                    };
                }

                // optionally get CiData from metaData in response
                if ( config.metaDataIncludesCiData ) {
                    const metaData = processData( response.data.metaData, dataContext + '-meta', 'CFG', 'keepExisting' );
                    for ( const itemId of metaData.items ) {
                        newElusiveData.metaData!.items![ itemId ] = ( elusiveData?.metaData?.items?.[ itemId ] ?? 0 ) + 1;
                    }
                    for ( const referenceId of metaData.references ) {
                        newElusiveData.metaData!.references![ referenceId ] = ( elusiveData?.metaData?.references?.[ referenceId ] ?? 0 ) + 1;
                    }
                }

                // get extra data from metaData object
                if ( config.extraMetaDataKeys ) {
                    newElusiveData.metaData = {
                        ...newElusiveData.metaData,
                        ..._.pick( response.data.metaData, config.extraMetaDataKeys )
                    };
                }

                // add root item to elusive data if not already exists
                if ( config.params?.rootItemId && !newElusiveData.data?.items?.[ config.params.rootItemId ] ) {
                    newElusiveData.data!.rootItemId = config.params.rootItemId;
                    newElusiveData.data!.items![ config.params.rootItemId ] = 1;
                }
            }

            dispatch( setElusiveData( elusiveDataContext, newElusiveData ) );
        }

        log.push( `processMeta: ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms` );

        if ( successCallback ) {
            void successCallback( response.data );
        }

        retVal = true;
    }

    log.push( `total: ${Math.round( 1000 * ( performance.now() - totalStartTime ) ) / 1000} ms` );
    log.push( ''.asLogLine( 100 ) );

    if ( debug ) {
        for ( const line of log ) {
            console.log( line );
        }
    }

    return retVal;
}
export type LoadData = typeof loadData;

export default loadData;
